import React, { useEffect } from "react";
import { Row, Col, Card, CardBody, Container, Spinner } from "reactstrap";
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

// Redux actions
// import { verifyEmail, registerReset } from "../../slices/auth/register/thunk";

//import images
import logoLight from "../../assets/images/logo-light-vertical.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { appName } from "../../Components/constants/default";
import {verifyEmail} from "../../slices/auth/register/thunk";
import {registerReset} from "../../slices/auth/register/reducer";

const VerifyEmail = () => {
  console.log('fff');
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get('token');

  // Redux selector
  const selectVerifyState = (state) => state.Account;
  const verifyStateSelector = createSelector(
    selectVerifyState,
    (register) => ({
      loading: register.loading,
      verificationSuccess: register.verificationSuccess,
      error: register.error,
      user: register.user
    })
  );

  const { loading, verificationSuccess, error, user } = useSelector(verifyStateSelector);

  useEffect(() => {
    if (!token) {
      return;
    }
    dispatch(verifyEmail(token));

    // Cleanup
    return () => {
      dispatch(registerReset());
    };
  }, [token, dispatch]);

  useEffect(() => {
    if (verificationSuccess) {
      const timer = setTimeout(() => {
        navigate('/admin/login');
      }, 9000);

      return () => clearTimeout(timer);
    }
  }, [verificationSuccess, navigate]);

  document.title = t('Email Verification') + " | " + appName;

  const getErrorMessage = () => {
    if (!error) return '';

    if (typeof error === 'string') {
      return error;
    }
    return t('An error occurred during verification');
  };

  const isTokenExpired = error && (
    error.includes('expired') ||
    error.includes('TOKEN_EXPIRED')
  );

  const isTokenInvalid = error && (
    error.includes('invalid') ||
    error.includes('TOKEN_INVALID')
  );

  const isAlreadyVerified = error && (
    error.includes('already') ||
    error.includes('ALREADY_VERIFIED')
  );

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mt-3 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={logoLight} alt="" height="70" />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">

                    {/* No Token */}
                    {!token && (
                      <div className="text-center mt-2">
                        <div className="mb-4">
                          <i className="ri-error-warning-line display-3 text-warning"></i>
                        </div>
                        <h5 className="text-warning mb-3">
                          {t("No Token Provided")}
                        </h5>
                        <p className="text-muted">
                          {t("No verification token was provided. Please check your email and click the verification link.")}
                        </p>
                        <div className="mt-4">
                          <Link
                            to="/admin/register"
                            className="btn btn-primary w-100"
                          >
                            {t("Go to Registration")}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Loading State */}
                    {token && loading && (
                      <div className="text-center mt-2">
                        <Spinner color="primary" className="mb-3" />
                        <h5 className="text-primary mb-3">
                          {t("Verifying your email...")}
                        </h5>
                        <p className="text-muted">
                          {t("Please wait while we verify your account.")}
                        </p>
                      </div>
                    )}

                    {/* Success State */}
                    {token && !loading && verificationSuccess && (
                      <div className="text-center mt-2">
                        <div className="mb-4">
                          <i className="ri-checkbox-circle-line display-3 text-success"></i>
                        </div>
                        <h5 className="text-success mb-3">
                          {t("Email Verified!")}
                        </h5>
                        <p className="text-muted mb-2">
                          {t("Your email has been successfully verified!")}
                        </p>
                        <p className="text-muted">
                          {t("Welcome")}{user?.username && <>, <strong>{user.username}</strong></>}!
                        </p>
                        <div className="mt-4">
                          <div className="alert alert-success" role="alert">
                            <i className="ri-information-line me-2"></i>
                            {t("You will be redirected to the login page shortly...")}
                          </div>
                        </div>
                        <div className="mt-3">
                          <Link
                            to="/admin/login"
                            className="btn btn-success w-100"
                          >
                            <i className="ri-login-box-line me-1"></i>
                            {t("Go to Login")}
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* Error State */}
                    {token && !loading && error && !verificationSuccess && (
                      <div className="text-center mt-2">
                        <div className="mb-4">
                          <i className="ri-close-circle-line display-3 text-danger"></i>
                        </div>
                        <h5 className="text-danger mb-3">
                          {t("Verification Failed")}
                        </h5>

                        <div className="alert alert-danger" role="alert">
                          {getErrorMessage()}
                        </div>

                        {isTokenExpired && (
                          <p className="text-muted mb-4">
                            {t("Your verification link has expired. Please register again to receive a new verification email.")}
                          </p>
                        )}

                        {isTokenInvalid && (
                          <p className="text-muted mb-4">
                            {t("The verification link is invalid. Please make sure you copied the entire link from your email.")}
                          </p>
                        )}

                        {isAlreadyVerified && (
                          <p className="text-muted mb-4">
                            {t("Your account is already verified. You can login now.")}
                          </p>
                        )}

                        <div className="mt-4">
                          {isAlreadyVerified ? (
                            <Link
                              to="/admin/login"
                              className="btn btn-success w-100"
                            >
                              <i className="ri-login-box-line me-1"></i>
                              {t("Go to Login")}
                            </Link>
                          ) : (
                            <Link
                              to="/admin/register"
                              className="btn btn-primary w-100"
                            >
                              <i className="ri-user-add-line me-1"></i>
                              {t("Register Again")}
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    <Link
                      to="/"
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      <i className="ri-arrow-left-line me-1"></i>
                      {t("Back to Home")}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default VerifyEmail;
